import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { starCount } from './numeric';
import type { WidgetProps } from './types';

/**
 * Étoiles `stars` : une note entière de 1 à 5, ou 10 quand la borne haute vaut 10 (comme la tablette). Un groupe radio (motif ARIA) : une seule étoile
 * dans l'ordre de tabulation, flèches pour passer d'une note à l'autre (en boucle), Début et Fin,
 * Espace ou Entrée pour choisir. Un champ facultatif s'efface ; un champ obligatoire, jamais.
 */
export function Stars({ id, field, value, onChange, disabled, invalid, describedBy, target, validation }: WidgetProps): ReactNode {
  const groupRef = useRef<HTMLDivElement>(null);
  if (field.type !== 'number' && field.type !== 'decimal' && field.type !== 'integer') return null;
  const count = starCount(validation, target);
  const current = typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= count ? value : null;
  const notes = Array.from({ length: count }, (_, index) => index + 1);
  const focusable = current ?? 1;

  const focusNote = (note: number): void => {
    groupRef.current?.querySelector<HTMLElement>(`[data-note="${note}"]`)?.focus();
  };
  const choose = (note: number): void => {
    if (note !== current) onChange(note);
    focusNote(note);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, note: number): void => {
    if (disabled) return;
    let next: number | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = note === count ? 1 : note + 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = note === 1 ? count : note - 1;
    else if (event.key === 'Home') next = 1;
    else if (event.key === 'End') next = count;
    else if (event.key === ' ' || event.key === 'Enter') next = note;
    if (next === undefined) return;
    event.preventDefault();
    choose(next);
  };

  return (
    <div className="ay-stars-row">
      <div ref={groupRef} id={id} className={`ay-stars${count === 10 ? ' ay-stars--ten' : ''}`} role="radiogroup" aria-labelledby={`${id}-label`} aria-describedby={describedBy} aria-invalid={invalid} aria-disabled={disabled || undefined}>
        {notes.map((note) => {
          const checked = current === note;
          return (
            <button
              key={note}
              type="button"
              role="radio"
              data-note={note}
              className={`ay-stars__star${current !== null && note <= current ? ' ay-stars__star--on' : ''}`}
              aria-checked={checked}
              aria-label={`${note} sur ${count}`}
              tabIndex={note === focusable ? 0 : -1}
              disabled={disabled}
              onClick={() => choose(note)}
              onKeyDown={(event) => onKeyDown(event, note)}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z" />
              </svg>
            </button>
          );
        })}
      </div>
      <span className="ay-stars__value" aria-hidden="true">
        {current === null ? `— / ${count}` : `${current} / ${count}`}
      </span>
      {current !== null && field.required !== true && !disabled && (
        <button type="button" className="ay-button ay-button--secondary ay-stars__clear" onClick={() => onChange(null)}>
          Effacer
        </button>
      )}
    </div>
  );
}
